import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { LightMode as LightModeIcon, DarkMode as DarkModeIcon } from '@mui/icons-material';
import { TechWorkStatus } from '../../../types/ticket';
import type { TechWorkStatus as TechWorkStatusType } from '../../../types/ticket';
import { useTheme } from '../../../contexts/ThemeContext';
import { useUserStatus } from '../../../contexts/UserStatusContext';
import { usePortal } from '../../../contexts/PortalContext';
import { PortalToggle } from '../PortalToggle';
import { NotificationBell } from '../../notifications';
import { LAYOUT_DIMENSIONS } from '../../../types/navigation';
import { transitions, zIndex as zIndexTokens } from '../../../theme/tokens';
import './SlimTopBar.css';

function formatElapsedTime(startTime: Date | undefined): string {
  if (!startTime) return '00:00';

  const totalSeconds = Math.floor((Date.now() - startTime.getTime()) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');

  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

interface StatusConfig {
  color: string;
  bgColor: string;
  label: string;
}

function getStatusConfig(status: TechWorkStatusType): StatusConfig {
  switch (status) {
    case TechWorkStatus.WORKING:
      return {
        color: '#107c10',
        bgColor: 'rgba(16, 124, 16, 0.1)',
        label: 'Working',
      };
    case TechWorkStatus.AVAILABLE:
      return {
        color: '#0078d4',
        bgColor: 'rgba(0, 120, 212, 0.1)',
        label: 'Available',
      };
    case TechWorkStatus.BREAK:
      return {
        color: '#ca5010',
        bgColor: 'rgba(202, 80, 16, 0.1)',
        label: 'Break',
      };
    case TechWorkStatus.LUNCH:
      return {
        color: '#8764b8',
        bgColor: 'rgba(135, 100, 184, 0.1)',
        label: 'Lunch',
      };
    case TechWorkStatus.OFFLINE:
    default:
      return {
        color: '#605e5c',
        bgColor: 'rgba(96, 94, 92, 0.1)',
        label: 'Offline',
      };
  }
}

interface SlimTopBarProps {
  currentUserName?: string;
}

export function SlimTopBar({ currentUserName = 'User' }: SlimTopBarProps) {
  const { theme, toggleTheme } = useTheme();
  const { workStatus, setWorkStatus, activeJob, workStartTime, signOut } = useUserStatus();
  const { activePortal, hasITAccess } = usePortal();
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const statusMenuRef = useRef<HTMLDivElement>(null);

  // Only show IT-specific UI elements when in IT portal
  const showITElements = activePortal === 'it';

  useEffect(() => {
    if (!activeJob || !workStartTime) {
      setElapsedTime('00:00');
      return;
    }
    setElapsedTime(formatElapsedTime(workStartTime));
    const interval = setInterval(() => setElapsedTime(formatElapsedTime(workStartTime)), 1000);
    return () => clearInterval(interval);
  }, [activeJob, workStartTime]);

  useEffect(() => {
    if (!isStatusMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setIsStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusMenuOpen]);

  const handleUserStatusChange = useCallback(
    (newStatus: TechWorkStatusType) => {
      setWorkStatus(newStatus);
      setIsStatusMenuOpen(false);
    },
    [setWorkStatus]
  );

  const handleSignOut = useCallback(() => {
    signOut();
    setIsStatusMenuOpen(false);
  }, [signOut]);

  const statusConfig = getStatusConfig(workStatus);

  return (
    <Box
      component="header"
      className="slim-topbar"
      sx={{
        height: LAYOUT_DIMENSIONS.TOPBAR_HEIGHT,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: zIndexTokens.sticky,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
      }}
    >
      {/* Left section - Logo/Title */}
      <Box className="slim-topbar__left">
        <span className="slim-topbar__title">
          {activePortal === 'it' ? 'IT Service Desk' : 'Help Center'}
        </span>
      </Box>

      {/* Center section - Portal Toggle (only for IT staff) */}
      {hasITAccess && (
        <Box className="slim-topbar__center">
          <PortalToggle />
        </Box>
      )}

      {/* Right section - Status, Theme, User */}
      <Box className="slim-topbar__right">
        {/* Current Status Indicator - IT Portal only */}
        {showITElements && (
          <div
            className={`slim-topbar__status ${activeJob ? 'slim-topbar__status--active' : ''}`}
            style={{
              backgroundColor: activeJob ? 'rgba(16, 124, 16, 0.1)' : statusConfig.bgColor,
              color: activeJob ? '#107c10' : statusConfig.color,
            }}
          >
            {activeJob ? (
              <>
                <span className="slim-topbar__status-dot" />
                <span>
                  Active: <strong>{activeJob.ticketNumber}</strong>
                </span>
                <span className="slim-topbar__status-divider">|</span>
                <span className="slim-topbar__timer">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="8" cy="8" r="6.5" />
                    <path d="M8 4.5v4l2.5 1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {elapsedTime}
                </span>
              </>
            ) : (
              <>
                <span className="slim-topbar__status-dot" />
                <span>{statusConfig.label}</span>
              </>
            )}
          </div>
        )}

        {/* Notification Bell */}
        <NotificationBell />

        {/* Theme Toggle */}
        <Tooltip title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            sx={{
              color: 'text.secondary',
              transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {theme === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* User Info with Status Menu */}
        <div className="slim-topbar__user" ref={statusMenuRef}>
          <button
            className="slim-topbar__user-button"
            onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}
            aria-expanded={isStatusMenuOpen}
            aria-haspopup="true"
          >
            <div className="slim-topbar__avatar">
              {currentUserName.charAt(0).toUpperCase()}
              <span className="slim-topbar__user-dot" style={{ backgroundColor: statusConfig.color }} />
            </div>
            <span className="slim-topbar__user-name">{currentUserName}</span>
            <svg
              className={`slim-topbar__chevron ${isStatusMenuOpen ? 'slim-topbar__chevron--open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Status Dropdown Menu */}
          {isStatusMenuOpen && (
            <div className="slim-topbar__menu">
              {/* IT Status options - only in IT portal */}
              {showITElements && (
                <>
                  <div className="slim-topbar__menu-header">Set Status</div>
                  <button
                    className={`slim-topbar__menu-item ${workStatus === TechWorkStatus.AVAILABLE ? 'slim-topbar__menu-item--active' : ''}`}
                    onClick={() => handleUserStatusChange(TechWorkStatus.AVAILABLE)}
                  >
                    <span className="slim-topbar__menu-dot" style={{ backgroundColor: '#0078d4' }} />
                    <span className="slim-topbar__menu-label">Available</span>
                    <span className="slim-topbar__menu-desc">Ready to take tickets</span>
                  </button>
                  <button
                    className={`slim-topbar__menu-item ${workStatus === TechWorkStatus.BREAK ? 'slim-topbar__menu-item--active' : ''}`}
                    onClick={() => handleUserStatusChange(TechWorkStatus.BREAK)}
                  >
                    <span className="slim-topbar__menu-dot" style={{ backgroundColor: '#ca5010' }} />
                    <span className="slim-topbar__menu-label">Break</span>
                    <span className="slim-topbar__menu-desc">Away for a short break</span>
                  </button>
                  <button
                    className={`slim-topbar__menu-item ${workStatus === TechWorkStatus.LUNCH ? 'slim-topbar__menu-item--active' : ''}`}
                    onClick={() => handleUserStatusChange(TechWorkStatus.LUNCH)}
                  >
                    <span className="slim-topbar__menu-dot" style={{ backgroundColor: '#8764b8' }} />
                    <span className="slim-topbar__menu-label">Lunch</span>
                    <span className="slim-topbar__menu-desc">Away for lunch</span>
                  </button>
                  <div className="slim-topbar__menu-divider" />
                </>
              )}
              <button className="slim-topbar__menu-item slim-topbar__menu-item--signout" onClick={handleSignOut}>
                <svg
                  className="slim-topbar__signout-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    d="M10 2H13a1 1 0 011 1v10a1 1 0 01-1 1h-3M6 12l-4-4 4-4M2 8h9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="slim-topbar__menu-label">Sign Out</span>
                <span className="slim-topbar__menu-desc">End your session</span>
              </button>
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
}
